import React, { useState, useRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { abortUpload, UploadProgress, UploadTask } from '@Utils/S3Storage'
import map from 'lodash/map'
import find from 'lodash/find'
import filter from 'lodash/filter'
import some from 'lodash/some'

import UploadArea from '@SiteComponents/UploadArea'
import UploadTable from './UploadTable'
import SubmitUrl from './SubmitUrl'
import { getFileMetadata } from '@Utils/GetFileDuration'
import ElementUploader, { IElementMediaDimensions, IElementUploaderOptions, IUploadedElement } from '@Utils/ElementUploader'
import Alert from '@Images/project_setup_form_alert.svg'
import CloseAlert from '@Images/upload_close_alert.svg'

import {
  IElementForProjectVersion,
  ISubmittingUrl,
} from '@SiteModules/ProjectVersionSettings/types'

import {
  addElementsForProjectVersions,
  removeFromElementsForProjectVersions,
} from '@SiteModules/ProjectVersionSettings/actions'

import {
  createElement,
  deleteElement,
} from '@SiteModules/Element/actions'

import './UploadAndImport.scss'

const BYTES_PER_GIGABYTE = 1073741824
const MAX_NUMBER_OF_SIMULTANEOUS_UPLOADS = 2
const MAX_NUMBER_OF_FILES_PER_SETUP = 10

export interface IFileToUpload {
  readonly elementId: string
  readonly file: File
  readonly hasStartedUpload: boolean
  readonly hasCompletedUpload: boolean
  readonly uploadProgress: number
  readonly mediaDimensions?: IElementMediaDimensions
  readonly uploadingRequest?: UploadTask
}

interface UploadAndImportProps {
  readonly elementsForProjectVersions: IElementForProjectVersion[]
  readonly showMissingFileAlert: boolean
  readonly showFilesTooLargeAlert: boolean
  readonly currentWorkspaceId: number
  readonly submittingUrls: ISubmittingUrl[]
  readonly maxFileSize: number
  setHasStartedFirstUploadOrSubmit(newSetHasStartedFirstUploadOrSubmit: boolean): void
  setShowMissingFileAlert(newShowMissingFileAlert: boolean): void
  setShowFilesTooLargeAlert(newShowFilesTooLargeAlert: boolean): void
}

const UploadAndImport: React.FC<UploadAndImportProps> = props => {
  const dispatch = useDispatch()

  const {
    elementsForProjectVersions,
    showMissingFileAlert,
    showFilesTooLargeAlert,
    currentWorkspaceId,
    submittingUrls,
    maxFileSize,
  } = props

  const hasFilesOrSubmittingUrls = elementsForProjectVersions.length > 0 || submittingUrls.length > 0

  const maxFileSizeInGB = (maxFileSize / BYTES_PER_GIGABYTE).toFixed(0)
  const [filesToUpload, setFilesToUpload] = useState<IFileToUpload[]>([])
  const filesToUploadRef = useRef<IFileToUpload[]>([])
  const isMountedRef = useRef(true)
  const [limitsUploadAlert, setLimitsUploadAlert] = useState<string | null>(null)
  const [showSubmitUrlInput, setShowSubmitUrlInput] = useState(true)

  const fileRef = useRef<HTMLInputElement>(null)

  const uploadCounter = filter(filesToUpload, fileToUpload => fileToUpload.hasStartedUpload && !fileToUpload.hasCompletedUpload).length
  const showLimitsUploadAlert = !!limitsUploadAlert
  const sizeAlertMessage = `Some files exceed the ${maxFileSizeInGB} GB size limit.`

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
      const abortPromises = filesToUploadRef.current
        .map(fileToUpload => fileToUpload.uploadingRequest)
        .filter((request): request is UploadTask => !!request)
        .map(request => abortUpload(request))

      void Promise.all(abortPromises)
    }
  }, [])

  const updateFilesToUpload = (update: (files: IFileToUpload[]) => IFileToUpload[]) => {
    const updatedFiles = update(filesToUploadRef.current)
    filesToUploadRef.current = updatedFiles
    setFilesToUpload(updatedFiles)
  }

  useEffect(() => {
    if (uploadCounter >= MAX_NUMBER_OF_SIMULTANEOUS_UPLOADS) { return }

    const nextFileToUpload = find(filesToUpload, file => !file.hasStartedUpload)

    if (!nextFileToUpload) { return }

    props.setHasStartedFirstUploadOrSubmit(true)

    const uploadingRequest = uploadFile(nextFileToUpload.file, nextFileToUpload.elementId, nextFileToUpload.mediaDimensions)
    updateHasStartedUpload(nextFileToUpload.elementId, uploadingRequest)
  }, [filesToUpload, elementsForProjectVersions.length, uploadCounter])

  useEffect(() => {
    if (elementsForProjectVersions.length === 0 && submittingUrls.length === 0) {
      setShowSubmitUrlInput(true)
    } else {
      setShowSubmitUrlInput(false)
    }
  }, [elementsForProjectVersions.length, submittingUrls.length])

  const updateUploadProgress = (elementId: string, uploadProgress: number) => {
    if (!Number.isFinite(uploadProgress)) { return }

    const boundedUploadProgress = Math.min(100, Math.max(0, uploadProgress))
    updateFilesToUpload(prevFiles =>
      map(prevFiles, file =>
        file.elementId === elementId
          ? { ...file, uploadProgress: boundedUploadProgress }
          : file
      )
    )
  }

  const updateHasStartedUpload = (elementId: string, uploadingRequest: UploadTask) => {
    updateFilesToUpload(prevFiles =>
      map(prevFiles, file =>
        file.elementId === elementId
          ? { ...file, hasStartedUpload: true, uploadingRequest }
          : file
      )
    )
  }

  const updateHasCompletedUpload = (elementId: string) => {
    updateFilesToUpload(prevFiles =>
      map(prevFiles, file =>
        file.elementId === elementId
          ? { ...file, hasCompletedUpload: true }
          : file
      )
    )
  }

  const openFileManager = () => {
    if (!fileRef) { return }
    if (!fileRef.current) { return }

    fileRef.current.click()
  }

  const handleUploadClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = (e.target as HTMLInputElement).files
    if (!files) { return }

    handleUpload(Array.from(files))

    e.target.value = '' // Set to an empty string to allow the upload of the same file again after aborting
  }

  const handleUpload = async (files: File[]) => {
    if (files.length + elementsForProjectVersions.length > MAX_NUMBER_OF_FILES_PER_SETUP) {
      setLimitsUploadAlert(`You can upload a maximum of ${MAX_NUMBER_OF_FILES_PER_SETUP} files.`)
      return
    }

    if (some(files, file => file.size > maxFileSize)) {
      setLimitsUploadAlert(sizeAlertMessage)
      return
    }

    const filesWithMetadata = await Promise.all(
      files.map(async file => {
        const metadata = await getFileMetadata(file) // silently returns undefined if it fails
        return { file, metadata }
      })
    )

    if (!isMountedRef.current) { return }

    const newFilesToUpload = filesWithMetadata.map(({ file, metadata }) => ({
      elementId: uuidv4(),
      file,
      hasStartedUpload: false,
      uploadProgress: 0,
      hasCompletedUpload: false,
      duration: metadata?.duration,
      mediaDimensions: metadata ? { width: metadata.width, height: metadata.height } : undefined,
    }))

    updateFilesToUpload(currentFiles => currentFiles.concat(newFilesToUpload))

    const newElementsToAdd = newFilesToUpload.map(({ elementId, file, duration }) => {
      const base = {
        elementId,
        name: file.name,
        size: file.size
      }
      return duration !== undefined ? { ...base, duration } : base
    })

    dispatch(addElementsForProjectVersions({ elementsToAdd: newElementsToAdd }))
  }

  const uploadFile = (file: File, elementId: string, mediaDimensions?: IElementMediaDimensions): UploadTask => {
    const options: IElementUploaderOptions = {
      uploadElementCallback: (params: IUploadedElement) => {
        const discardAfterCreate = !isMountedRef.current

        if (!discardAfterCreate) {
          updateUploadProgress(elementId, 100)
          updateHasCompletedUpload(elementId)
        }

        dispatch(createElement({
          ...params,
          workspaceId: currentWorkspaceId,
          elementId,
          discardAfterCreate,
        }))
      },
      showProgressCallback: (data: UploadProgress) => {
        if (!isMountedRef.current) { return }
        const loadedBytes = data.loaded
        const totalBytes = data.total ?? file.size
        if (typeof loadedBytes !== 'number' || !Number.isFinite(loadedBytes) || !Number.isFinite(totalBytes) || totalBytes <= 0) { return }

        const uploadProgress = loadedBytes / totalBytes * 100
        updateUploadProgress(elementId, uploadProgress)
      },
      workspaceId: currentWorkspaceId,
      mediaDimensions,
    }

    return new ElementUploader(file, options).call()
  }

  const handleFileDrop = (filesList: FileList) => {
    const files = Array.from(filesList).filter((item) => {
      return item.type.startsWith('video/') || item.type.startsWith('audio/')
    })

    if (files.length > 0) {
      handleUpload(files)
    }
  }

  const handleRemoveFile = (elementId: string, projectId?: string) => {
    const fileToRemove = find(filesToUploadRef.current, file => file.elementId === elementId)

    // Remove the item from the queue before cancellation can race with upload
    // completion. The creation saga owns cleanup if completion wins that race.
    updateFilesToUpload(currentFiles => filter(currentFiles, file => file.elementId !== elementId))
    dispatch(removeFromElementsForProjectVersions({ elementId }))

    if (projectId) {
      dispatch(deleteElement({ elementId }))
    }

    if (fileToRemove?.uploadingRequest) {
      void abortUpload(fileToRemove.uploadingRequest)
    }
  }

  const displaySubmitUrlInput = () => {
    setShowSubmitUrlInput(true)
  }

  const resetLimitsUploadAlert = () => {
    setLimitsUploadAlert(null)
  }

  const resetFilesTooLargeAlert = () => {
    props.setShowFilesTooLargeAlert(false)
  }

  const renderUploadTable = () => {
    return (
      <UploadTable
        elementsForProjectVersions={elementsForProjectVersions}
        filesToUpload={filesToUpload}
        handleFileDrop={handleFileDrop}
        handleRemoveFile={handleRemoveFile}
        submittingUrls={submittingUrls}
        showAlert={showFilesTooLargeAlert}
      />
    )
  }

  const renderUploadArea = () => {
    return (
      <UploadArea
        showAlert={showMissingFileAlert || showLimitsUploadAlert}
        handleFileDrop={handleFileDrop}
        openFileManager={openFileManager}
      />
    )
  }

  const renderAlert = (alertMessage: string, resetAlert: () => void) => {
    return (
      <div className="SetupProjectPopup__alert">
        <Alert />
        {alertMessage}
        <CloseAlert
          className="UploadAndImport__close-alert"
          onClick={resetAlert}
        />
      </div>
    )
  }

  const renderMaxSize = () => {
    return (
      <div className="UploadAndImport__text UploadAndImport__text-upload">
        {`The maximum size of a file should be ${maxFileSizeInGB} GB`}
      </div>
    )
  }

  const renderUploadAndImportLinks = () => {
    return (
      <div className="UploadAndImport__text UploadAndImport__text-upload">
        To add a new file, drag and drop it to the table,
        {' '}
        <span onClick={openFileManager}>
          click here
        </span>
        {' '}
        or
        {' '}
        {showSubmitUrlInput ? (
          'paste a URL'
        ) : (
          <span onClick={displaySubmitUrlInput}>
            add a URL here
          </span>
        )}
        .
      </div>
    )
  }

  return (
    <div className="UploadAndImport">
      <div className="SetupProjectPopup__section-header">
        {hasFilesOrSubmittingUrls ? 'Project files' : 'Upload your video'}
      </div>
      {hasFilesOrSubmittingUrls ? renderUploadAndImportLinks() : renderMaxSize()}
      {hasFilesOrSubmittingUrls ? renderUploadTable() : renderUploadArea()}
      {showLimitsUploadAlert && renderAlert(limitsUploadAlert, resetLimitsUploadAlert)}
      {showFilesTooLargeAlert && renderAlert(sizeAlertMessage, resetFilesTooLargeAlert)}
      <SubmitUrl
        currentWorkspaceId={currentWorkspaceId}
        maxFileSizeInGB={maxFileSizeInGB}
        elementsForProjectVersions={elementsForProjectVersions}
        maxNumberOfFilesPerSetup={MAX_NUMBER_OF_FILES_PER_SETUP}
        hasFilesOrSubmittingUrls={hasFilesOrSubmittingUrls}
        showMissingFileAlert={showMissingFileAlert}
        setHasStartedFirstUploadOrSubmit={props.setHasStartedFirstUploadOrSubmit}
        setShowMissingFileAlert={props.setShowMissingFileAlert}
        renderLimitsAlert={renderAlert}
        showSubmitUrlInput={showSubmitUrlInput}
      />
      <input
        ref={fileRef}
        type="file"
        data-testid="project-media-input"
        multiple
        className="UploadAndImport__upload-input"
        onChange={handleUploadClick}
        accept=".mov,video/mp4,video/x-m4v,audio/*,video/*,.mkv"
      />
    </div>
  )
}

export default UploadAndImport
